/**
 * finance.types.ts - 财务管理模块类型定义
 *
 * 功能说明：
 * - 分包成本、合同、付款审批、质保金、预付款等财务相关类型
 *
 * 依赖关系：
 * - 被分包管理、合同管理、付款审批等模块使用
 *
 * 关键注释索引：
 * - [分包成本] - 见下方 SubcontractCost 接口
 * - [支付节点] - 见下方 SubcontractPayNode 接口
 * - [付款审批] - 见下方 PaymentApprovalData 接口
 *
 * 创建日期：2026-05-03
 * 作者：AI Assistant
 */

// ==================== 分包结算相关枚举 ====================

/**
 * [分包结算方式] 分包结算方式枚举
 */
export enum SubcontractSettlementMethod {
  TOTAL_PACKAGE = 'total_package',
  UNIT_PRICE_PACKAGE = 'unit_price_package'
}

/**
 * [分包状态] 分包成本状态枚举
 */
export enum SubcontractCostStatus {
  ACTIVE = 'active',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled'
}

/**
 * [分包口径] 分包成本口径枚举
 */
export enum SubcontractCostCaliber {
  RESPONSIBILITY = 'responsibility',
  SETTLEMENT = 'settlement',
  PAYMENT = 'payment'
}

/**
 * [结算状态] 分包结算状态枚举
 */
export enum SubcontractSettlementStatus {
  UNSETTLED = 'unsettled',
  PARTIAL = 'partial',
  FULL = 'full'
}

/**
 * [施工类型] 分包施工类型枚举
 */
export enum SubcontractConstructionType {
  GARDEN = '园林',
  CIVIL = '土建',
  FACADE = '外墙',
  OTHER = '其它'
}

// ==================== 支付节点相关枚举 ====================

/**
 * [支付节点类型] 支付节点类型枚举
 */
export enum PayNodeType {
  TIME = 'time',
  PROGRESS = 'progress'
}

/**
 * [验收状态] 验收状态枚举
 */
export enum VerificationStatus {
  NOT_REQUIRED = 'not_required',
  PENDING = 'pending',
  PASSED = 'passed',
  PARTIAL = 'partial',
  FAILED = 'failed'
}

/**
 * [节点状态] 支付节点状态枚举
 */
export enum PayNodeStatus {
  PENDING = 'pending',
  PARTIAL = 'partial',
  PAID = 'paid',
  WRITTEN_OFF = 'written_off'
}

// ==================== 合同相关枚举 ====================

/**
 * [合同类型] 合同类型枚举
 */
export enum ContractType {
  MAIN = 'main',
  AMENDMENT = 'amendment',
  CHANGE = 'change',
}

/**
 * [合同状态] 合同状态枚举
 */
export enum ContractStatus {
  DRAFT = 'draft',
  ACTIVE = 'active',
  COMPLETED = 'completed',
  TERMINATED = 'terminated',
}

/**
 * [付款里程碑状态] 付款里程碑状态枚举
 */
export enum PaymentMilestoneStatus {
  PENDING = 'pending',
  PARTIAL = 'partial',
  PAID = 'paid',
}

/**
 * [合同变更类型] 合同变更类型枚举
 */
export enum ContractChangeType {
  INCREASE = 'increase',
  DECREASE = 'decrease',
  AMENDMENT = 'amendment',
}

// ==================== 付款审批相关枚举 ====================

/**
 * [审批步骤类型] 审批步骤类型枚举
 */
export enum ApprovalStepType {
  CONSENSUS = 'consensus',
  ANY_ONE = 'any_one',
}

/**
 * [审批状态] 审批状态枚举
 */
export enum ApprovalStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  DELEGATED = 'delegated',
  SKIPPED = 'skipped',
}

/**
 * [付款审批状态] 付款审批状态枚举
 */
export enum PaymentApprovalStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  WITHDRAWN = 'withdrawn',
  CANCELLED = 'cancelled',
}

// ==================== 质保金相关枚举 ====================

/**
 * [质保金状态] 质保金状态枚举
 */
export enum RetentionStatus {
  WITHHELD = 'withheld',
  RELEASABLE = 'releasable',
  PARTIAL = 'partial',
  COMPLETED = 'completed',
  DEDUCTED = 'deducted',
}

/**
 * [质保金释放状态] 质保金释放状态枚举
 */
export enum RetentionReleaseStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  RELEASED = 'released',
  REJECTED = 'rejected',
}

// ==================== 预付款相关枚举 ====================

/**
 * [预付款状态] 预付款状态枚举
 */
export enum AdvancePaymentStatus {
  NOT_ISSUED = 'not_issued',
  ISSUED = 'issued',
  RECOVERING = 'recovering',
  CLEARED = 'cleared',
  CANCELLED = 'cancelled',
}

/**
 * [保函状态] 保函状态枚举
 */
export enum GuaranteeStatus {
  NOT_SUBMITTED = 'not_submitted',
  VALID = 'valid',
  EXPIRING_SOON = 'expiring_soon',
  EXPIRED = 'expired',
}

// ==================== 分包相关接口 ====================

/**
 * [验收项配置] 验收项配置接口
 */
export interface SubcontractVerificationItemConfig {
  itemName: string;
  standard: string;
  isRequired: boolean;
}

/**
 * [验收项结果] 验收项结果接口
 */
export interface SubcontractVerificationItemResult {
  itemName: string;
  status: 'pass' | 'fail' | 'not_applicable';
  note?: string;
}

/**
 * [验收记录] 验收记录接口
 */
export interface SubcontractVerificationRecord {
  _id?: string;
  verificationDate: string;
  verifier: string;
  verifierName?: string;
  itemResults: SubcontractVerificationItemResult[];
  result: 'pass' | 'partial' | 'fail';
  reason?: string;
  approvedPaymentAmount: number;
  note?: string;
}

/**
 * [付款记录] 付款记录接口
 */
export interface SubcontractPaymentRecord {
  _id?: string;
  verificationId?: string;
  verificationDate?: string;
  amount: number;
  paidDate: string;
  paidMethod?: 'transfer' | 'cash' | 'bill';
  receiptNo?: string;
  note?: string;
}

/**
 * [支付节点] 支付节点接口
 *
 * @description 表示分包合同中的付款节点
 * - nodeType: 触发类型（按时间/按进度）
 * - requireVerification: 是否需要验收
 * - verificationHistory: 验收历史
 * - paymentRecords: 付款记录
 */
export interface SubcontractPayNode {
  nodeName: string;
  nodeType: PayNodeType;
  nodeOrder: number;
  plannedDate?: string;
  plannedProgress?: number;
  requireVerification: boolean;
  verificationItems: SubcontractVerificationItemConfig[];
  defaultVerifier?: string;
  defaultVerifierName?: string;
  verificationStatus: VerificationStatus;
  plannedAmount: number;
  paidAmount: number;
  paidDate?: string;
  status: PayNodeStatus;
  verificationHistory: SubcontractVerificationRecord[];
  paymentRecords: SubcontractPaymentRecord[];
  linkedCalculationItems?: number[];
  note?: string;
}

/**
 * [分包商选项] 分包商选择项接口
 */
export interface SubcontractorOption {
  _id: string;
  name: string;
  shortName?: string;
  contactPerson?: string;
  phone?: string;
  status?: 'active' | 'inactive' | 'blacklisted';
}

/**
 * [计算项] 分包计算项接口
 */
export interface SubcontractCalculationItem {
  workContent: string;
  unitPrice: number;
  measurementUnit?: string;
  actualCompletedQuantity: number;
  itemTotal: number;
}

/**
 * [调整项] 分包调整项接口
 */
export interface SubcontractAdjustmentItem {
  description: string;
  amount: number;
}

/**
 * [分包成本] 分包成本主数据接口
 *
 * @description 记录分包合同的完整信息
 */
export interface SubcontractCost {
  _id: string;
  projectId: string;
  projectName?: string;
  subcontractorId?: string | SubcontractorOption;
  contractNo?: string;
  contractName: string;
  contractSignDate?: string;
  contractOwner?: string;
  contractSigner?: string;
  counterpartyName?: string;
  counterpartySigner?: string;
  costDate: string;
  constructionType?: SubcontractConstructionType | null;
  settlementMethod: SubcontractSettlementMethod;
  contractAmount: number;
  increaseAmount: number;
  decreaseAmount: number;
  increaseItems: SubcontractAdjustmentItem[];
  decreaseItems: SubcontractAdjustmentItem[];
  unitPrice: number;
  actualCompletedQuantity: number;
  calculationItems: SubcontractCalculationItem[];
  settlementAmount: number;
  settledAmount: number;
  paidAmount: number;
  unpaidAmount: number;
  paymentProgress: number;
  costCaliber: SubcontractCostCaliber;
  settlementStatus: SubcontractSettlementStatus;
  status: SubcontractCostStatus;
  payNodes: SubcontractPayNode[];
  description?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * [创建分包] 创建分包成本的数据类型
 */
export interface CreateSubcontractCostData {
  projectId: string;
  subcontractorId?: string;
  contractNo?: string;
  contractName: string;
  contractSignDate?: string;
  contractOwner?: string;
  contractSigner?: string;
  counterpartyName?: string;
  counterpartySigner?: string;
  costDate?: string;
  constructionType?: SubcontractConstructionType | null;
  settlementMethod: SubcontractSettlementMethod;
  contractAmount?: number;
  increaseAmount?: number;
  decreaseAmount?: number;
  increaseItems?: SubcontractAdjustmentItem[];
  decreaseItems?: SubcontractAdjustmentItem[];
  unitPrice?: number;
  actualCompletedQuantity?: number;
  calculationItems?: Array<Pick<SubcontractCalculationItem, 'workContent' | 'unitPrice' | 'measurementUnit' | 'actualCompletedQuantity'>>;
  settledAmount?: number;
  paidAmount?: number;
  status?: SubcontractCostStatus;
  costCaliber?: SubcontractCostCaliber;
  payNodes?: SubcontractPayNode[];
  description?: string;
}

/**
 * [更新分包] 更新分包成本的数据类型
 */
export type UpdateSubcontractCostData = Partial<CreateSubcontractCostData>;

// ==================== 合同相关接口 ====================

/**
 * [付款里程碑] 合同付款里程碑接口
 */
export interface PaymentMilestone {
  _id?: string;
  nodeName: string;
  plannedAmount: number;
  paidAmount: number;
  plannedDate?: string;
  paidDate?: string;
  status: PaymentMilestoneStatus;
  note?: string;
}

/**
 * [合同变更记录] 合同变更记录接口
 */
export interface ContractChangeRecord {
  _id?: string;
  changeType: ContractChangeType;
  description: string;
  changeAmount: number;
  changeDate: string;
  reason?: string;
}

/**
 * [合同附件] 合同附件接口
 */
export interface ContractAttachment {
  _id?: string;
  fileName: string;
  fileUrl: string;
  fileType: string;
  fileSize?: number;
  uploadedAt?: string;
}

/**
 * [项目合同] 项目合同主数据接口
 *
 * @description 记录项目合同的完整信息
 */
export interface ProjectContract {
  _id: string;
  projectId: string | { _id: string; name: string; projectCode?: string };
  projectName?: string;
  contractNo?: string;
  contractName: string;
  contractType: ContractType;
  partyA: string;
  partyB: string;
  totalAmount: number;
  paidAmount: number;
  paymentProgress?: number;
  unpaidAmount?: number;
  signingDate?: string;
  startDate?: string;
  endDate?: string;
  status: ContractStatus;
  paymentMilestones: PaymentMilestone[];
  changeRecords: ContractChangeRecord[];
  attachments: ContractAttachment[];
  description?: string;
  createdBy?: string | { _id: string; username: string; realName?: string };
  createdAt: string;
  updatedAt: string;
}

/**
 * [合同统计] 项目合同汇总统计
 */
export interface ProjectContractSummary {
  contractCount: number;
  totalAmount: number;
  totalPaidAmount: number;
  paymentProgress: number;
}

/**
 * [合同查询] 合同查询参数
 */
export interface ContractQueryParams {
  projectId?: string;
  status?: ContractStatus;
  contractType?: ContractType;
  keyword?: string;
  page?: number;
  limit?: number;
}

/**
 * [创建合同] 创建合同的数据类型
 */
export interface CreateContractData {
  projectId: string;
  contractNo?: string;
  contractName: string;
  contractType?: ContractType;
  partyA: string;
  partyB: string;
  totalAmount: number;
  paidAmount?: number;
  signingDate?: string;
  startDate?: string;
  endDate?: string;
  status?: ContractStatus;
  paymentMilestones?: PaymentMilestone[];
  changeRecords?: ContractChangeRecord[];
  attachments?: ContractAttachment[];
  description?: string;
}

/**
 * [更新合同] 更新合同的数据类型
 */
export type UpdateContractData = Partial<CreateContractData>;

// ==================== 付款审批相关接口 ====================

/**
 * [审批人信息] 审批人信息接口
 */
export interface ApproverInfo {
  userId: string;
  userName: string;
  status: 'pending' | 'approved' | 'rejected' | 'delegated';
  delegatedTo?: string;
  delegatedToName?: string;
  approvedAt?: string;
  comment?: string;
}

/**
 * [审批步骤] 审批步骤数据接口
 */
export interface ApprovalStepData {
  stepIndex: number;
  stepName: string;
  stepType: ApprovalStepType;
  approvers: ApproverInfo[];
  requiredApprovals: number;
  status: ApprovalStatus;
  approvedCount: number;
  rejectedCount: number;
  slaMinutes?: number;
  startedAt?: string;
  completedAt?: string;
  comment?: string;
}

/**
 * [审批历史] 审批历史记录接口
 */
export interface ApprovalHistoryRecord {
  action: 'submit' | 'approve' | 'reject' | 'delegate' | 'withdraw' | 'cancel' | 'remind';
  userId: string;
  userName: string;
  stepIndex?: number;
  comment?: string;
  timestamp: string;
}

/**
 * [付款审批] 付款审批数据接口
 *
 * @description 表示分包付款审批流程的完整数据
 */
export interface PaymentApprovalData {
  id: string;
  subcontractCostId: string;
  payNodeIndex: number;
  nodeName: string;
  approvalAmount: number;
  status: PaymentApprovalStatus;
  currentStepIndex: number;
  currentStepName?: string;
  totalSteps: number;
  completedStepCount: number;
  steps: ApprovalStepData[];
  history: ApprovalHistoryRecord[];
  submitterId: string;
  submitterName: string;
  submittedAt?: string;
  approvedAt?: string;
  rejectedAt?: string;
  rejectedReason?: string;
  rejecterId?: string;
  rejecterName?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * [付款审批别名] 付款审批的别名导出
 */
export type PaymentApproval = PaymentApprovalData;

/**
 * [审批步骤别名] 审批步骤的别名导出
 */
export type ApprovalStep = ApprovalStepData;

// ==================== 质保金相关接口 ====================

/**
 * [质保金释放计划] 质保金释放计划接口
 */
export interface RetentionReleaseSchedule {
  phaseName: string;
  releaseRatio: number;
  releaseAmount?: number;
  scheduledDate?: string;
  isDue?: boolean;
  isReleased?: boolean;
}

/**
 * [质保金释放记录] 质保金释放记录接口
 */
export interface RetentionReleaseRecord {
  id: string;
  amount: number;
  reason?: string;
  status: RetentionReleaseStatus;
  requestUserId: string;
  requestUserName: string;
  requestDate: string;
  approvedUserId?: string;
  approvedUserName?: string;
  approvedDate?: string;
  releasedDate?: string;
  rejectReason?: string;
  note?: string;
}

/**
 * [质保金扣款记录] 质保金扣款记录接口
 */
export interface RetentionDeductRecord {
  id: string;
  amount: number;
  reason: string;
  qualityIssueNo?: string;
  deductDate: string;
  deductUserId: string;
  deductUserName: string;
}

/**
 * [质保金变更记录] 质保金变更记录接口
 */
export interface RetentionChangeRecord {
  type: 'withhold' | 'release' | 'deduct';
  amount: number;
  balance: number;
  operatorId: string;
  operatorName: string;
  date: string;
  note?: string;
}

/**
 * [质保金数据] 质保金完整数据接口
 *
 * @description 记录质保金的完整信息
 */
export interface RetentionData {
  id: string;
  subcontractCostId: string;
  contractName: string;
  subcontractorName: string;
  projectId: string;
  projectName: string;
  contractAmount: number;
  retentionRatio: number;
  totalRetained: number;
  totalReleased: number;
  totalDeducted: number;
  pendingRelease: number;
  currentBalance: number;
  status: RetentionStatus;
  defectLiabilityStartDate?: string;
  defectLiabilityEndDate?: string;
  defectLiabilityMonths: number;
  releaseSchedule?: RetentionReleaseSchedule[];
  releaseRecords: RetentionReleaseRecord[];
  deductRecords: RetentionDeductRecord[];
  changeHistory: RetentionChangeRecord[];
  isExpired?: boolean;
  liabilityProgress?: number;
  daysRemaining?: number;
  createdAt: string;
  updatedAt: string;
}

// ==================== 预付款相关接口 ====================

/**
 * [保函信息] 保函信息接口
 */
export interface GuaranteeInfo {
  id?: string;
  guaranteeNo: string;
  issuingBank: string;
  guaranteedAmount: number;
  startDate: string;
  expiryDate: string;
  beneficiary: string;
  status: GuaranteeStatus;
  submittedDate?: string;
  note?: string;
}

/**
 * [预付款释放记录] 预付款释放记录接口
 */
export interface AdvancePaymentReleaseRecord {
  id: string;
  progressPaymentId?: string;
  progressPaymentNodeName?: string;
  releaseAmount: number;
  releaseDate: string;
  operatorId: string;
  operatorName: string;
  note?: string;
}

/**
 * [预付款变更记录] 预付款变更记录接口
 */
export interface AdvancePaymentChangeRecord {
  type: 'issue' | 'release' | 'cancel';
  amount: number;
  balance: number;
  operatorId: string;
  operatorName: string;
  date: string;
  note?: string;
}

/**
 * [预付款数据] 预付款完整数据接口
 *
 * @description 记录预付款的完整信息
 */
export interface AdvancePaymentData {
  id: string;
  subcontractCostId: string;
  contractName: string;
  subcontractorName: string;
  projectId: string;
  projectName: string;
  contractAmount: number;
  advancePaymentRatio: number;
  totalAmount: number;
  issuedAmount: number;
  recoveredAmount: number;
  pendingRecovery: number;
  status: AdvancePaymentStatus;
  recoveryMethod: 'proportional' | 'cumulative';
  monthlyRecoveryRatio: number;
  guarantee: GuaranteeInfo | null;
  releaseRecords: AdvancePaymentReleaseRecord[];
  changeHistory: AdvancePaymentChangeRecord[];
  issuedDate?: string;
  clearedDate?: string;
  isGuaranteeRequired?: boolean;
  recoveryProgress?: number;
  createdAt: string;
  updatedAt: string;
}
